import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import type { SourceType } from '@trello/analytics-types';
import { ChunkLoadErrorBoundary } from '@trello/error-boundaries';
import { Key } from '@trello/keybindings';
import { currentLocale } from '@trello/locale';

import type { EntrypointId } from './EntrypointId';
import type { AkFeedbackCollectorProps } from './FeedbackCollector';
import { showFeedbackFlag } from './showFeedbackFlag';
import { useAdditionalFields } from './useAdditionalFields';
import { useFeedbackCollectorAnalytics } from './useFeedbackCollectorAnalytics';
import { useLazyFeedbackCollector } from './useLazyFeedbackCollector';

/**
 * Hook that manages the lifecycle of a feedback collector dialog, including
 * opening/closing state, analytics events, keyboard dismissal, and lazy loading
 * of the underlying Atlaskit `FeedbackCollector` component.
 *
 * @param options - Configuration for the feedback collector.
 * @param options.entrypointId - Identifier for the feedback entrypoint, used for
 *   analytics and to associate feedback with a specific feature area.
 * @param options.source - Analytics source type indicating where the feedback
 *   collector was triggered from.
 * @param options.feedbackCollectorProps - Partial props forwarded to the underlying
 *   Atlaskit `FeedbackCollector` component.
 * @param options.attributes - Optional key-value context attributes included as
 *   additional fields in the feedback submission.
 * @param options.getFeedbackContextOverride - Optional callback invoked when the
 *   collector opens to retrieve a context string (typically stringified JSON) that
 *   overrides the default feedback context.
 * @param options.onClose - Optional callback invoked after the collector is closed.
 * @param options.onSubmit - Optional callback invoked after feedback is submitted.
 *
 * @returns An object containing:
 *   - `FeedbackCollector` — A component that renders the feedback dialog when open.
 *   - `showFeedbackCollector` — A function to open the feedback collector.
 *   - `updateFeedbackContext` — A function to programmatically update the feedback
 *     context override string.
 */
export const useFeedbackCollector = ({
  entrypointId,
  source,
  feedbackCollectorProps,
  attributes,
  getFeedbackContextOverride,
  onClose,
  onSubmit: onSubmitCallback,
}: {
  entrypointId: EntrypointId;
  source: SourceType;
  feedbackCollectorProps: Partial<AkFeedbackCollectorProps>;
  attributes?: Record<string, string>;
  // Returns string (which is generally stringified JSON)
  getFeedbackContextOverride?: () => string;
  onClose?: () => void;
  onSubmit?: () => void;
}): {
  FeedbackCollector: FunctionComponent;
  showFeedbackCollector: () => void;
  updateFeedbackContext: (context: string | undefined) => void;
} => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackContextOverride, setFeedbackContextOverride] = useState<
    string | undefined
  >(undefined);
  const { onCloseAnalyticsEvent, onSubmitAnalyticsEvent } =
    useFeedbackCollectorAnalytics({ source, entrypointId });
  const LazyFeedbackCollector = useLazyFeedbackCollector({ preload: isOpen });

  const onCloseCollector = useCallback(() => {
    onCloseAnalyticsEvent();
    setIsOpen(false);
    setFeedbackContextOverride(undefined);
    onClose?.();
  }, [onClose, onCloseAnalyticsEvent]);

  const onSubmit = useCallback(() => {
    onSubmitAnalyticsEvent();
    showFeedbackFlag({ id: 'feedbackCollectorSuccessFlag' });
    setIsOpen(false);
    setFeedbackContextOverride(undefined);
    onSubmitCallback?.();
  }, [onSubmitAnalyticsEvent, onSubmitCallback]);

  const handleEscapePress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === Key.Escape && isOpen) {
        onCloseCollector();
      }
    },
    [onCloseCollector, isOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscapePress);
    return (): void => {
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, [handleEscapePress]);

  useEffect(() => {
    // When the feedback collector is opened, fetch attributes via overrides
    // if one exists
    if (isOpen && getFeedbackContextOverride) {
      // TODO ENGHEALTH-2180: baselined when the rule was enabled
      setFeedbackContextOverride(getFeedbackContextOverride()); // oxlint-disable-line react/set-state-in-effect
    }
  }, [isOpen, getFeedbackContextOverride]);

  const additionalFields = useAdditionalFields({
    contextAttributes: attributes,
    contextOverride: feedbackContextOverride,
    entrypointId,
  });

  const additionalFieldsRef = useRef(additionalFields);
  additionalFieldsRef.current = additionalFields;

  const feedbackCollectorPropsRef = useRef(feedbackCollectorProps);
  feedbackCollectorPropsRef.current = feedbackCollectorProps;

  const renderFeedbackCollector = useCallback(
    () =>
      isOpen ? (
        <ChunkLoadErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <LazyFeedbackCollector
              showTypeField={false}
              {...feedbackCollectorPropsRef.current}
              onClose={onCloseCollector}
              onSubmit={onSubmit}
              entrypointId={entrypointId}
              additionalFields={additionalFieldsRef.current}
              locale={currentLocale}
            />
          </Suspense>
        </ChunkLoadErrorBoundary>
      ) : null,
    [LazyFeedbackCollector, isOpen, entrypointId, onCloseCollector, onSubmit],
  );

  const showFeedbackCollector = useCallback((): void => {
    setIsOpen(true);
  }, []);

  return {
    FeedbackCollector: renderFeedbackCollector,
    showFeedbackCollector,
    updateFeedbackContext: setFeedbackContextOverride,
  };
};
